import React, { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Working hours drawn behind one day column, and dragged out to create more.
 *
 * Ported from the Israeli calendar, where the behaviour was settled over a
 * day of iteration; the reasoning in the comments is what that produced, so
 * it is kept rather than rediscovered.
 *
 * Sits UNDER the meetings on purpose: a meeting booked outside the band is
 * then obvious at a glance, which is the reason to show working hours on the
 * calendar rather than only in a form.
 */

export interface WorkingBlock {
  /** The availability row this block is. For a CANCELLED occurrence it is
   *  the weekly row that would have produced it - a cancellation has no row
   *  of its own, so its identity belongs to the pattern. */
  id?: number | null;
  start_time: string;   // "09:00"
  end_time: string;     // "17:00"
  /** Where it came from - the standing week, this date alone, an occurrence
   *  this date has DELETED, or a span this date has CLOSED. */
  source?: 'weekly' | 'date' | 'cancelled' | 'blocked';
  comment?: string | null;
}

interface AvailabilityLayerProps {
  blocks: WorkingBlock[];
  /** Hours this date will NOT give, drawn in red, of two kinds that `source`
   *  tells apart because they are undone differently:
   *
   *    'cancelled'  an occurrence of the pattern deleted from this date.
   *                 Drawn struck through and dashed - an absence. Restoring
   *                 it gives the occurrence back.
   *    'blocked'    hours kept but closed. Drawn solid, because the row is
   *                 there and so are the hours. Unblocking lifts it.
   *
   *  Shown rather than hidden either way: a cancellation that simply vanished
   *  could only be put back by re-entering the hours from memory, and a block
   *  nobody can see is a block nobody remembers to lift. */
  blocked?: WorkingBlock[];
  /** First hour on the grid - the pixel origin. */
  firstHour: number;
  pixelsPerHour: number;
  /** Dragging is offered only to someone who may actually save. */
  editable: boolean;
  /** The dragged range, once the mouse is released. Nothing is saved here;
   *  the caller confirms it first. */
  onCreate?: (start: string, end: string) => void;
  onEditBlock?: (index: number, block: WorkingBlock) => void;
  /** Put a cancelled occurrence back. */
  onReviveBlock?: (block: WorkingBlock) => void;
  /** Lift a blocked span, leaving the hours where they are. */
  onUnblock?: (block: WorkingBlock) => void;
  /** Opens the editor for this day without dragging. */
  onEdit?: () => void;
}

// Matches the 5-minute step the editor offers, so a dragged time is one the
// picker can also produce - a drag landing on 09:07 could not then be nudged
// without retyping it.
const SNAP_MINUTES = 5;

/** Three states, each its own hue rather than a tint of one. They mean
 *  genuinely different things - "this is the usual week", "this date is
 *  different", "this was cancelled" - and a calendar is read at a glance,
 *  where tints of one colour read as the same thing. */
const BLOCK_COLOURS = {
  weekly: { fill: '#dcebe9', border: '#a9ccc8', stripe: '#5E9AA5', text: '#2D5A60' },
  date:   { fill: '#fdf0d9', border: '#e3c187', stripe: '#C98A2E', text: '#8a5a12' },
} as const;
const BLOCKED = { fill: '#fdeaea', border: '#e9a8a8', stripe: '#C53030', text: '#9b2c2c' };

const colourFor = (source?: 'weekly' | 'date' | 'cancelled' | 'blocked') =>
  source === 'blocked' || source === 'cancelled' ? BLOCKED
    : source === 'date' ? BLOCK_COLOURS.date
    : BLOCK_COLOURS.weekly;

/** Below this a drag is a mis-click, not an intent to create hours. */
const MIN_DRAG_PX = 6;

const toLabel = (minutes: number): string => {
  const m = Math.max(0, Math.min(24 * 60, minutes));
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
};

const hhmm = (t: string) => t.slice(0, 5);

export const AvailabilityLayer: React.FC<AvailabilityLayerProps> = ({
  blocks, blocked = [], firstHour, pixelsPerHour, editable,
  onCreate, onEdit, onEditBlock, onReviveBlock, onUnblock,
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [drag, setDrag] = useState<{ from: number; to: number } | null>(null);

  const minutesAt = useCallback((clientY: number): number => {
    const box = ref.current?.getBoundingClientRect();
    if (!box) return firstHour * 60;
    const raw = firstHour * 60 + ((clientY - box.top) / pixelsPerHour) * 60;
    return Math.round(raw / SNAP_MINUTES) * SNAP_MINUTES;
  }, [firstHour, pixelsPerHour]);

  // Where the gesture began, so a press that turns out to be a click can be
  // handed back to whatever is underneath.
  const startedOn = useRef<EventTarget | null>(null);

  // The mousedown is taken on the CELL, in the capture phase, so it arrives
  // before the meeting that happens to be under the pointer. Without this a
  // drag cannot begin on top of an appointment - and the hours someone wants
  // to draw are very often exactly where one already sits.
  useEffect(() => {
    const cell = ref.current?.parentElement;
    if (!cell || !editable) return;

    const down = (e: MouseEvent) => {
      if (e.button !== 0) return;
      const target = e.target as HTMLElement | null;
      if (target?.dataset?.availabilityBlock === 'true') return;
      startedOn.current = e.target;
      const y = minutesAt(e.clientY);
      setDrag({ from: y, to: y });
      e.preventDefault();
    };

    cell.addEventListener('mousedown', down, true);
    return () => cell.removeEventListener('mousedown', down, true);
  }, [editable, minutesAt]);

  // Once a drag starts it is followed on the WINDOW, not on this element.
  // Meetings are painted above this layer, so a drag that crossed one would
  // stop receiving events and die halfway - making it impossible to draw
  // hours around an existing appointment, exactly where they are most needed.
  useEffect(() => {
    if (!drag) return;

    const move = (e: MouseEvent) => {
      setDrag((d) => (d ? { ...d, to: minutesAt(e.clientY) } : d));
    };

    const up = () => {
      setDrag((d) => {
        if (!d) return null;
        const from = Math.min(d.from, d.to);
        const to = Math.max(d.from, d.to);
        const px = ((to - from) / 60) * pixelsPerHour;
        if (px < MIN_DRAG_PX) {
          // A click, not a drag. If it landed on a meeting, that meeting's
          // own click should happen and nothing here should.
          const onLayer = startedOn.current === ref.current
            || (startedOn.current instanceof Node && ref.current?.contains(startedOn.current))
            || startedOn.current === ref.current?.parentElement;
          if (onLayer) onEdit?.();
        } else {
          // A real drag: swallow the click that follows, or releasing over a
          // meeting would open it as well as creating the hours.
          const swallow = (ev: MouseEvent) => {
            ev.stopPropagation();
            ev.preventDefault();
          };
          window.addEventListener('click', swallow, true);
          window.setTimeout(() => window.removeEventListener('click', swallow, true), 0);
          onCreate?.(toLabel(from), toLabel(to));
        }
        startedOn.current = null;
        return null;
      });
    };

    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') setDrag(null); };

    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    window.addEventListener('keydown', key);
    return () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      window.removeEventListener('keydown', key);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drag !== null, minutesAt, pixelsPerHour]);

  const topOf = (t: string) => {
    const [h, m] = hhmm(t).split(':').map(Number);
    return ((h * 60 + m) - firstHour * 60) / 60 * pixelsPerHour;
  };
  const heightOf = (b: WorkingBlock) =>
    Math.max(2, topOf(b.end_time) - topOf(b.start_time));

  const preview = drag && (() => {
    const from = Math.min(drag.from, drag.to);
    const to = Math.max(drag.from, drag.to);
    return {
      top: (from - firstHour * 60) / 60 * pixelsPerHour,
      height: ((to - from) / 60) * pixelsPerHour,
      label: `${toLabel(from)}–${toLabel(to)}`,
    };
  })();

  return (
    <div
      ref={ref}
      style={{
        position: 'absolute',
        inset: 0,
        // Only claims the pointer when it can act on it, so a read-only
        // calendar behaves exactly as it did before.
        pointerEvents: editable ? 'auto' : 'none',
        cursor: editable ? 'crosshair' : 'default',
        zIndex: 0,
      }}
    >
      {/* An edge stripe per block, painted ABOVE the meetings. The band
          itself sits underneath them, so wherever an appointment overlapped
          the working hours the hours simply disappeared - which is the one
          place you most need to see them. The stripe is narrow and takes no
          pointer events, so it marks the full extent without obscuring. */}
      {blocks.map((b, i) => (
        <div
          key={`wh-edge-${i}`}
          style={{
            position: 'absolute',
            top: topOf(b.start_time),
            height: heightOf(b),
            left: 0,
            width: 5,
            backgroundColor: colourFor(b.source).stripe,
            zIndex: 3,
            pointerEvents: 'none',
          }}
        />
      ))}

      {blocks.map((b, i) => {
        const c = colourFor(b.source);
        return (
          <div
            key={`wh-${i}`}
            data-availability-block="true"
            title={[
              `Working hours ${hhmm(b.start_time)}–${hhmm(b.end_time)}`,
              b.source === 'date' ? 'this date only' : '',
              b.comment || '',
              editable ? 'click to edit' : '',
            ].filter(Boolean).join(' — ')}
            onMouseDown={editable && onEditBlock
              // Stops the drag that would otherwise start underneath, so
              // clicking a block edits it instead of drawing over it.
              ? (e) => { e.stopPropagation(); e.preventDefault(); }
              : undefined}
            onClick={editable && onEditBlock
              ? (e) => { e.stopPropagation(); onEditBlock(i, b); }
              : undefined}
            style={{
              position: 'absolute',
              top: topOf(b.start_time),
              height: heightOf(b),
              left: 0,
              right: 0,
              backgroundColor: c.fill,
              borderTop: `1px solid ${c.border}`,
              borderBottom: `1px solid ${c.border}`,
              cursor: editable && onEditBlock ? 'pointer' : 'inherit',
              pointerEvents: editable && onEditBlock ? 'auto' : 'none',
            }}
          >
            {heightOf(b) > 22 && (
              <div style={{
                fontSize: 10, padding: '2px 6px', color: c.text,
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}>
                {hhmm(b.start_time)}–{hhmm(b.end_time)}
                {b.comment ? ` · ${b.comment}` : ''}
              </div>
            )}
          </div>
        );
      })}

      {/* A blocked span's own edge stripe, above the meetings - for the same
          reason the working hours have one. A block is very often put on
          time that already has something in it, and a band hidden under
          that something is a band nobody sees. */}
      {blocked.filter((b) => b.source === 'blocked').map((b, i) => (
        <div
          key={`wh-shut-edge-${i}`}
          style={{
            position: 'absolute',
            top: topOf(b.start_time),
            height: heightOf(b),
            left: 0,
            width: 5,
            backgroundColor: BLOCKED.stripe,
            zIndex: 3,
            pointerEvents: 'none',
          }}
        />
      ))}

      {/* The refused hours. Blocked is solid and labelled, because the row
          and the hours are both there; cancelled is dashed and struck
          through, because it is an absence. */}
      {blocked.map((b, i) => {
        const shut = b.source === 'blocked';
        const act = shut ? onUnblock : onReviveBlock;
        return (
          <div
            key={`wh-blocked-${i}`}
            data-availability-block="true"
            title={(shut
                ? `Blocked ${hhmm(b.start_time)}–${hhmm(b.end_time)}`
                : `Cancelled ${hhmm(b.start_time)}–${hhmm(b.end_time)}`)
              + (shut && b.comment ? ` — ${b.comment}` : '')
              + (editable && act ? (shut ? ' — click to unblock'
                                         : ' — click to restore') : '')}
            onMouseDown={editable && act
              ? (e) => { e.stopPropagation(); e.preventDefault(); }
              : undefined}
            onClick={editable && act
              // Opens the editor rather than acting on the spot: a single
              // click giving refused hours away is too easy to do by accident.
              ? (e) => { e.stopPropagation(); act(b); }
              : undefined}
            style={{
              position: 'absolute',
              top: topOf(b.start_time),
              height: heightOf(b),
              left: 0,
              right: 0,
              backgroundColor: BLOCKED.fill,
              border: `1px ${shut ? 'solid' : 'dashed'} ${BLOCKED.border}`,
              // A block sits INSIDE working hours that are still drawn, so it
              // goes above them or the band would be painted over it. A
              // cancellation has no band under it and needs no lift.
              zIndex: shut ? 1 : 0,
              cursor: editable && act ? 'pointer' : 'inherit',
              pointerEvents: editable && act ? 'auto' : 'none',
            }}
          >
            {heightOf(b) > 22 && (
              <div style={{
                fontSize: 10, padding: '2px 6px', color: BLOCKED.text,
                textDecoration: shut ? 'none' : 'line-through',
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}>
                {hhmm(b.start_time)}–{hhmm(b.end_time)}
                {shut ? ' blocked' : ''}
                {shut && b.comment ? ` · ${b.comment}` : ''}
              </div>
            )}
          </div>
        );
      })}

      {preview && (
        <div style={{
          position: 'absolute',
          top: preview.top,
          height: Math.max(2, preview.height),
          left: 0,
          right: 0,
          backgroundColor: 'rgba(94,154,165,0.28)',
          border: '1px solid #5E9AA5',
          zIndex: 4,
          pointerEvents: 'none',
        }}>
          <div style={{ fontSize: 10, padding: '2px 6px', color: '#2D5A60' }}>
            {preview.label}
          </div>
        </div>
      )}
    </div>
  );
};

export default AvailabilityLayer;
